import { useState, useMemo, useCallback, useEffect } from "react";
import { 
  Search, Plus, Minus, ShoppingBag, Users, Check, X, ChevronLeft, Bell, RefreshCw, Edit2, User, LogOut, Eye, Printer, Receipt, CreditCard, AlertTriangle,
  Package, Beer, Pizza, Coffee, IceCream, Sandwich, Wine, Cigarette,
  Droplets, Zap, Cookie, Soup, Cherry, UtensilsCrossed, Beef, Fish,
  Salad, Cake, Pill, ShoppingBasket, Candy, Apple, Milk, Croissant,
  Donut, Popcorn, Martini, GlassWater, CupSoda, Banana, Carrot, Egg,
  Ham, Drumstick, Flame, LucideIcon, Loader2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useTables, RestaurantTable } from "@/hooks/useTables";
import { useCommands, Command } from "@/hooks/useCommands";
import { useProducts } from "@/hooks/useProducts";
import { useCategories } from "@/hooks/useCategories";
import { useOrders } from "@/hooks/useOrders";
import { usePaymentMethods } from "@/hooks/usePaymentMethods";
import { useWaiterCalls } from "@/hooks/useWaiterCalls";
import { useWaiterCallNotifications } from "@/hooks/useWaiterCallNotifications";
import { WaiterCallNotificationPanel } from "@/components/notifications/WaiterCallNotificationPanel";
import { usePullToRefresh } from "@/hooks/usePullToRefresh";
import { PullToRefreshIndicator } from "@/components/pwa/PullToRefreshIndicator";
import { TableNicknameDialog } from "@/components/tables/TableNicknameDialog";
import { PeopleCountDialog } from "@/components/pdv/PeopleCountDialog";
import { useAuth } from "@/hooks/useAuth";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useCurrentOrderItems } from "@/hooks/useCurrentOrderItems";
import { useCommandEntryValidation } from "@/hooks/useCommandEntryValidation";
import { useWaiterWorkSession } from "@/hooks/useWaiterWorkSession";
import { useUserRole } from "@/hooks/useUserRole";
import { WaiterCheckinScreen } from "@/components/garcom/WaiterCheckinScreen";
import { SessionStatusBadge } from "@/components/garcom/SessionStatusBadge";
import { printConferenceBill } from "@/lib/order-printing";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { InstallPrompt } from "@/components/pwa/InstallPrompt";
import { GarcomProductGrid } from "@/components/garcom/GarcomProductGrid";
import { GarcomComboGrid } from "@/components/garcom/GarcomComboGrid";
import { ConnectionStatusIndicator } from "@/components/garcom/ConnectionStatusIndicator";
import { ComboDetailSheet } from "@/components/combos/ComboDetailSheet";
import { WidgetErrorBoundary } from "@/components/garcom/WidgetErrorBoundary";
import { useInternalCombos, type InternalComboWithDetails } from "@/hooks/useInternalCombos";
import type { ComboSelection, ComboCartItem } from "@/hooks/useCart";
import type { Tables } from "@/integrations/supabase/types";

type Product = Tables<"products">;

// Icon mapping for categories
const ICON_MAP: Record<string, LucideIcon> = {
  "package": Package,
  "beer": Beer,
  "pizza": Pizza,
  "coffee": Coffee,
  "ice-cream": IceCream,
  "sandwich": Sandwich,
  "wine": Wine,
  "cigarette": Cigarette,
  "droplets": Droplets,
  "zap": Zap,
  "cookie": Cookie,
  "soup": Soup,
  "cherry": Cherry,
  "utensils-crossed": UtensilsCrossed,
  "beef": Beef,
  "fish": Fish,
  "salad": Salad,
  "cake": Cake,
  "pill": Pill,
  "shopping-bag": ShoppingBasket,
  "candy": Candy,
  "apple": Apple,
  "milk": Milk,
  "croissant": Croissant,
  "donut": Donut,
  "popcorn": Popcorn,
  "martini": Martini,
  "glass-water": GlassWater,
  "cup-soda": CupSoda,
  "banana": Banana,
  "carrot": Carrot,
  "egg": Egg,
  "ham": Ham,
  "drumstick": Drumstick,
  "flame": Flame,
};

// Color styles for unselected and selected states
const COLOR_STYLES: Record<string, { unselected: string; selected: string }> = {
  blue: {
    unselected: "bg-blue-100 border-blue-300 text-blue-700 hover:bg-blue-200",
    selected: "bg-blue-500 text-white border-blue-600 hover:bg-blue-600",
  },
  green: {
    unselected: "bg-green-100 border-green-300 text-green-700 hover:bg-green-200",
    selected: "bg-green-500 text-white border-green-600 hover:bg-green-600",
  },
  orange: {
    unselected: "bg-orange-100 border-orange-300 text-orange-700 hover:bg-orange-200",
    selected: "bg-orange-500 text-white border-orange-600 hover:bg-orange-600",
  },
  purple: {
    unselected: "bg-purple-100 border-purple-300 text-purple-700 hover:bg-purple-200",
    selected: "bg-purple-500 text-white border-purple-600 hover:bg-purple-600",
  },
  pink: {
    unselected: "bg-pink-100 border-pink-300 text-pink-700 hover:bg-pink-200",
    selected: "bg-pink-500 text-white border-pink-600 hover:bg-pink-600",
  },
  cyan: {
    unselected: "bg-cyan-100 border-cyan-300 text-cyan-700 hover:bg-cyan-200",
    selected: "bg-cyan-500 text-white border-cyan-600 hover:bg-cyan-600",
  },
  amber: {
    unselected: "bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200",
    selected: "bg-amber-500 text-white border-amber-600 hover:bg-amber-600",
  },
  red: {
    unselected: "bg-red-100 border-red-300 text-red-700 hover:bg-red-200",
    selected: "bg-red-500 text-white border-red-600 hover:bg-red-600",
  },
  indigo: {
    unselected: "bg-indigo-100 border-indigo-300 text-indigo-700 hover:bg-indigo-200",
    selected: "bg-indigo-500 text-white border-indigo-600 hover:bg-indigo-600",
  },
  teal: {
    unselected: "bg-teal-100 border-teal-300 text-teal-700 hover:bg-teal-200",
    selected: "bg-teal-500 text-white border-teal-600 hover:bg-teal-600",
  },
  lime: {
    unselected: "bg-lime-100 border-lime-300 text-lime-700 hover:bg-lime-200",
    selected: "bg-lime-500 text-white border-lime-600 hover:bg-lime-600",
  },
  rose: {
    unselected: "bg-rose-100 border-rose-300 text-rose-700 hover:bg-rose-200",
    selected: "bg-rose-500 text-white border-rose-600 hover:bg-rose-600",
  },
};

function getIconComponent(iconName: string | null | undefined): LucideIcon {
  if (!iconName) return Package;
  return ICON_MAP[iconName] || Package;
}

function getColorStyles(colorName: string | null | undefined, isSelected: boolean): string {
  if (!colorName || !COLOR_STYLES[colorName]) {
    return isSelected
      ? "bg-primary text-primary-foreground hover:bg-primary/90"
      : "bg-background border-input hover:bg-accent hover:text-accent-foreground";
  }
  return isSelected ? COLOR_STYLES[colorName].selected : COLOR_STYLES[colorName].unselected;
}

interface CartItem {
  product: Product;
  quantity: number;
  notes: string;
}

type ViewMode = "selection" | "products" | "cart";
type SelectionMode = "tables" | "commands";
type ProductViewTab = "products" | "combos";

export default function GarcomPage() {
  const { tables, isLoading: tablesLoading, updateTable, fetchTables } = useTables();
  const { commands, isLoading: commandsLoading, updateCommand, fetchCommands } = useCommands();
  const { products, isLoading: productsLoading, fetchProducts } = useProducts();
  const { categories, fetchCategories } = useCategories();
  const { sendItemsToOrder } = useOrders();
  const { paymentMethods, fetchPaymentMethods } = usePaymentMethods();
  const { pendingCalls } = useWaiterCalls();
  const { user, signOut } = useAuth();
  const [userName, setUserName] = useState<string | null>(null);
  useWaiterCallNotifications(); // Enable real-time notifications

  // Fetch combos for the waiter app
  const { data: combos = [], isLoading: combosLoading } = useInternalCombos('pdv');

  // Session management for waiters
  const { hasActiveSession, isLoading: sessionLoading } = useWaiterWorkSession();
  const { isWaiter, isLoading: roleLoading } = useUserRole();

  // Fetch user profile name
  useEffect(() => {
    const fetchUserName = async () => {
      if (user?.id) {
        const { data } = await supabase
          .from("profiles")
          .select("full_name")
          .eq("id", user.id)
          .single();
        setUserName(data?.full_name || user.email?.split("@")[0] || null);
      }
    };
    fetchUserName();
  }, [user]);

  // Fetch data on mount
  useEffect(() => {
    fetchTables();
    fetchCommands();
    fetchProducts();
    fetchCategories();
    fetchPaymentMethods();
  }, [fetchTables, fetchCommands, fetchProducts, fetchCategories, fetchPaymentMethods]);

  // Pull to refresh
  const handleRefresh = useCallback(async () => {
    await Promise.all([
      fetchTables(),
      fetchCommands(),
      fetchProducts(),
      fetchCategories(),
      fetchPaymentMethods(),
    ]);
    toast.success("Dados atualizados!");
  }, [fetchTables, fetchCommands, fetchProducts, fetchCategories, fetchPaymentMethods]);

  const {
    containerRef,
    pullDistance,
    isRefreshing,
    progress,
    handlers: pullHandlers,
  } = usePullToRefresh({ onRefresh: handleRefresh });

  const [selectedTable, setSelectedTable] = useState<RestaurantTable | null>(null);
  const [selectedCommand, setSelectedCommand] = useState<Command | null>(null);
  const [selectionMode, setSelectionMode] = useState<SelectionMode>("tables");
  const [cart, setCart] = useState<CartItem[]>([]);
  const [comboCart, setComboCart] = useState<ComboCartItem[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("selection");
  const [productViewTab, setProductViewTab] = useState<ProductViewTab>("products");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [currentItemForNote, setCurrentItemForNote] = useState<Product | null>(null);
  const [itemNote, setItemNote] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [nicknameDialogOpen, setNicknameDialogOpen] = useState(false);
  const [orderNotes, setOrderNotes] = useState("");
  const [showConsumptionDialog, setShowConsumptionDialog] = useState(false);
  const [isPrintingConference, setIsPrintingConference] = useState(false);
  const [peopleCount, setPeopleCount] = useState<number | undefined>(undefined);
  const [showPeopleDialog, setShowPeopleDialog] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{ type: "table" | "command"; item: RestaurantTable | Command } | null>(null);
  
  // Combo selection sheet state
  const [selectedCombo, setSelectedCombo] = useState<InternalComboWithDetails | null>(null);
  const [comboSheetOpen, setComboSheetOpen] = useState(false);

  // Get restaurant context
  const { activeRestaurant } = useRestaurantContext();

  // Fetch sent items for selected table or command
  const { sentItems, isLoading: loadingSentItems } = useCurrentOrderItems(
    selectedTable?.id,
    selectedCommand?.id
  );

  // Calculate total of sent items
  const sentItemsTotal = useMemo(() => {
    return sentItems.reduce((sum, item) => sum + Number(item.total_price), 0);
  }, [sentItems]);

  // Filter active tables
  const activeTables = useMemo(() => {
    return tables.filter((t) => t.is_active);
  }, [tables]);

  // Filter active commands and apply entry validation
  const { hasActiveEntry, getUsableCommands, requireEntry, getEntryForCommand } = useCommandEntryValidation();

  const usableCommands = useMemo(() => {
    const activeOnly = commands.filter((c) => c.is_active);
    return getUsableCommands(activeOnly);
  }, [commands, getUsableCommands]);

  // For display: show all active but indicate which are usable
  const activeCommands = useMemo(() => {
    return commands.filter((c) => c.is_active);
  }, [commands]);

  // Filter active products
  const activeProducts = useMemo(() => {
    return products.filter((p) => p.is_active);
  }, [products]);

  // Filter products by category and search
  const filteredProducts = useMemo(() => {
    return activeProducts.filter((product) => {
      const matchesCategory =
        selectedCategory === "all" || product.category_id === selectedCategory;
      const matchesSearch =
        searchQuery === "" ||
        product.name.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [activeProducts, selectedCategory, searchQuery]);

  // Active categories (with products)
  const activeCategories = useMemo(() => {
    const categoriesWithProducts = new Set(
      activeProducts.map((p) => p.category_id).filter(Boolean)
    );
    return categories.filter(
      (c) => c.is_active && categoriesWithProducts.has(c.id)
    );
  }, [categories, activeProducts]);

  // Cart helpers
  const cartTotal = useMemo(() => {
    const productTotal = cart.reduce(
      (sum, item) => sum + Number(item.product.price) * item.quantity,
      0
    );
    const comboTotal = comboCart.reduce(
      (sum, item) => sum + item.calculatedPrice * item.quantity,
      0
    );
    return productTotal + comboTotal;
  }, [cart, comboCart]);

  const cartItemCount = useMemo(() => {
    const productCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const comboCount = comboCart.reduce((sum, item) => sum + item.quantity, 0);
    return productCount + comboCount;
  }, [cart, comboCart]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value);
  };

  // Select table
  const handleSelectTable = (table: RestaurantTable) => {
    // If table is not occupied, ask for people count
    if (table.status !== "occupied") {
      setPendingSelection({ type: "table", item: table });
      setShowPeopleDialog(true);
    } else {
      completeTableSelection(table);
    }
  };

  const completeTableSelection = (table: RestaurantTable, count?: number) => {
    setSelectedTable(table);
    setSelectedCommand(null);
    setCart([]);
    setComboCart([]);
    setPeopleCount(count);
    setViewMode("products");
  };

  // Select command - check if entry required
  const handleSelectCommand = (command: Command) => {
    // Check if command entry is required but not opened
    if (requireEntry && !hasActiveEntry(command.number) && command.status !== "in_use") {
      toast.error(`Comanda ${command.number} não foi aberta`, {
        description: "Vá em 'Abertura de Comandas' para liberar esta comanda.",
      });
      return;
    }
    
    // If command is not in use, ask for people count
    if (command.status !== "in_use") {
      setPendingSelection({ type: "command", item: command });
      setShowPeopleDialog(true);
    } else {
      completeCommandSelection(command);
    }
  };

  const completeCommandSelection = (command: Command, count?: number) => {
    setSelectedCommand(command);
    setSelectedTable(null);
    setCart([]);
    setComboCart([]);
    setPeopleCount(count);
    setViewMode("products");
  };

  const handlePeopleConfirm = (count: number) => {
    if (pendingSelection) {
      if (pendingSelection.type === "table") {
        completeTableSelection(pendingSelection.item as RestaurantTable, count);
      } else {
        completeCommandSelection(pendingSelection.item as Command, count);
      }
      setPendingSelection(null);
    }
  };

  // Get selected item info for display
  const selectedItemLabel = useMemo(() => {
    if (selectedTable) return `Mesa ${selectedTable.number}`;
    if (selectedCommand) return `Comanda ${selectedCommand.number}`;
    return "";
  }, [selectedTable, selectedCommand]);

  const selectedItemName = useMemo(() => {
    if (selectedTable) return selectedTable.name;
    if (selectedCommand) return selectedCommand.customer_name;
    return null;
  }, [selectedTable, selectedCommand]);

  const isSelectedItemOccupied = useMemo(() => {
    if (selectedTable) return selectedTable.status === "occupied";
    if (selectedCommand) return selectedCommand.status === "in_use";
    return false;
  }, [selectedTable, selectedCommand]);

  // ========== VERIFICAÇÃO DE CHECK-IN (GARÇONS) ==========
  // Bloquear UI até saber o role e o status da sessão
  if (roleLoading || sessionLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Verificar se garçom precisa fazer check-in
  if (isWaiter && !hasActiveSession) {
    return <WaiterCheckinScreen onCheckinSuccess={() => window.location.reload()} />;
  }
  // =========================================================

  // Add product to cart
  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1, notes: "" }];
    });
  };

  // Remove from cart
  const removeFromCart = (productId: string) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === productId);
      if (existing && existing.quantity > 1) {
        return prev.map((item) =>
          item.product.id === productId
            ? { ...item, quantity: item.quantity - 1 }
            : item
        );
      }
      return prev.filter((item) => item.product.id !== productId);
    });
  };

  // Get quantity in cart
  const getCartQuantity = (productId: string) => {
    const item = cart.find((i) => i.product.id === productId);
    return item?.quantity || 0;
  };

  // Combo cart helpers
  const getComboCartCount = (comboId: string) => {
    return comboCart.filter((c) => c.combo.id === comboId).reduce((sum, c) => sum + c.quantity, 0);
  };

  const handleComboClick = (combo: InternalComboWithDetails) => {
    setSelectedCombo(combo);
    setComboSheetOpen(true);
  };

  const handleAddComboToCart = (
    combo: InternalComboWithDetails,
    selection: ComboSelection,
    calculatedPrice: number,
    quantity: number,
    notes?: string
  ) => {
    setComboCart((prev) => {
      const selectionKey = JSON.stringify(selection);
      const existingIndex = prev.findIndex(
        (item) => item.combo.id === combo.id && JSON.stringify(item.selection) === selectionKey && item.notes === notes
      );

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + quantity,
        };
        return updated;
      }

      return [...prev, { combo, quantity, notes, selection, calculatedPrice }];
    });
  };

  const removeComboFromCart = (index: number) => {
    setComboCart((prev) => prev.filter((_, i) => i !== index));
  };

  const updateComboQuantity = (index: number, newQty: number) => {
    if (newQty <= 0) {
      removeComboFromCart(index);
      return;
    }
    setComboCart((prev) =>
      prev.map((item, i) => (i === index ? { ...item, quantity: newQty } : item))
    );
  };

  // Open note dialog
  const openNoteDialog = (product: Product) => {
    setCurrentItemForNote(product);
    const existingItem = cart.find((i) => i.product.id === product.id);
    setItemNote(existingItem?.notes || "");
    setNoteDialogOpen(true);
  };

  // Save note
  const saveNote = () => {
    if (currentItemForNote) {
      setCart((prev) =>
        prev.map((item) =>
          item.product.id === currentItemForNote.id
            ? { ...item, notes: itemNote }
            : item
        )
      );
    }
    setNoteDialogOpen(false);
    setCurrentItemForNote(null);
    setItemNote("");
  };

  // Submit order - uses sendItemsToOrder to add to existing order or create new one
  const handleSubmitOrder = async () => {
    if ((!selectedTable && !selectedCommand) || (cart.length === 0 && comboCart.length === 0)) return;

    setIsSubmitting(true);
    setSubmitStatus("sending");

    try {
      const orderItems = cart.map((item) => ({
        product: item.product,
        quantity: item.quantity,
        notes: item.notes,
      }));

      // Convert combo cart items to order items format
      const comboOrderItems = comboCart.map((item) => {
        // Build description from selections
        let selectionNotes = "";
        if (item.selection.slotSelections) {
          selectionNotes = item.selection.slotSelections
            .map(s => `${s.slotLabel}: ${s.selectedProducts.map(p => p.productName).join(", ")}`)
            .join(" | ");
        } else if (item.selection.selectedFlavors) {
          selectionNotes = `Sabores: ${item.selection.selectedFlavors.map(f => f.productName).join(", ")}`;
        } else if (item.selection.selectedChoices) {
          selectionNotes = item.selection.selectedChoices
            .map(c => `${c.choiceLabel}: ${c.productName}`)
            .join(" | ");
        }

        const fullNotes = [selectionNotes, item.notes].filter(Boolean).join(" | ");

        return {
          product: {
            id: item.combo.id,
            name: item.combo.name,
            price: item.calculatedPrice,
          } as Product,
          quantity: item.quantity,
          notes: fullNotes,
          isCombo: true,
          comboId: item.combo.id,
          comboSelections: item.selection,
        };
      });

      const allItems = [...orderItems, ...comboOrderItems];

      const orderConfig = selectedTable
        ? {
            type: "table" as const,
            tableNumber: selectedTable.number,
            customerName: selectedTable.name || undefined,
            waiterName: userName || user?.email?.split("@")[0] || "Garçom",
            notes: orderNotes || undefined,
          }
        : {
            type: "tab" as const,
            tabNumber: selectedCommand!.number,
            customerName: selectedCommand!.customer_name || undefined,
            waiterName: userName || user?.email?.split("@")[0] || "Garçom",
            notes: orderNotes || undefined,
          };

      const result = await sendItemsToOrder(
        allItems,
        orderConfig,
        selectedTable?.id,
        selectedCommand?.id,
        peopleCount
      );

      if (result) {
        setSubmitStatus("success");
        setCart([]);
        setComboCart([]);
        setOrderNotes("");
        setPeopleCount(undefined);
        setViewMode("selection");
        setSelectedTable(null);
        setSelectedCommand(null);
        toast.success(`Pedido enviado para ${selectedItemLabel}!`);
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Erro ao enviar pedido:", error);
      setSubmitStatus("error");
      toast.error("Erro ao enviar pedido. Tente novamente.");
    } finally {
      setIsSubmitting(false);
      // Reset status after a delay
      setTimeout(() => setSubmitStatus("idle"), 2000);
    }
  };

  // Print conference bill
  const handlePrintConference = async () => {
    if ((!selectedTable && !selectedCommand) || !activeRestaurant?.id) return;
    
    setIsPrintingConference(true);
    try {
      const success = await printConferenceBill(
        activeRestaurant.id,
        undefined,
        selectedTable?.id,
        selectedCommand?.id
      );
      
      if (success) {
        setShowConsumptionDialog(false);
      }
    } catch (error) {
      console.error("Erro ao imprimir conferência:", error);
      toast.error("Erro ao imprimir conferência");
    } finally {
      setIsPrintingConference(false);
    }
  };

  // Back button handler
  const handleBack = () => {
    if (viewMode === "cart") {
      setViewMode("products");
    } else if (viewMode === "products") {
      setSelectedTable(null);
      setSelectedCommand(null);
      setCart([]);
      setViewMode("selection");
    }
  };

  // Render selection view (tables or commands)
  const renderSelectionView = () => (
    <div className="flex flex-col h-full w-full min-w-0 overflow-x-hidden">
      <div className="p-3 border-b bg-primary text-primary-foreground">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold flex items-center gap-2">
              {selectionMode === "tables" ? (
                <Users className="h-5 w-5" />
              ) : (
                <CreditCard className="h-5 w-5" />
              )}
              {selectionMode === "tables" ? "Mesas" : "Comandas"}
            </h1>
            {userName && (
              <p className="text-xs opacity-90 flex items-center gap-1">
                <User className="h-3 w-3" />
                Olá, {userName}!
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <WidgetErrorBoundary>
              {isWaiter && <SessionStatusBadge />}
            </WidgetErrorBoundary>
            <WidgetErrorBoundary>
              <ConnectionStatusIndicator />
            </WidgetErrorBoundary>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={async () => {
                await signOut();
                window.location.href = "/auth";
              }}
              title="Sair"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Toggle between tables and commands */}
      <div className="p-2 border-b flex gap-1">
        <Button
          size="sm"
          variant={selectionMode === "tables" ? "default" : "outline"}
          onClick={() => setSelectionMode("tables")}
          className="flex-1 gap-1 h-9"
        >
          <Users className="h-4 w-4" />
          Mesas
        </Button>
        <Button
          size="sm"
          variant={selectionMode === "commands" ? "default" : "outline"}
          onClick={() => setSelectionMode("commands")}
          className="flex-1 gap-1 h-9"
        >
          <CreditCard className="h-4 w-4" />
          Comandas
        </Button>
      </div>

      {/* Waiter Calls Notifications */}
      {pendingCalls.length > 0 && (
        <div className="p-2 border-b">
          <WidgetErrorBoundary>
            <WaiterCallNotificationPanel variant="inline" />
          </WidgetErrorBoundary>
        </div>
      )}

      <div
        ref={containerRef}
        className="flex-1 overflow-y-auto overflow-x-hidden relative w-full"
        {...pullHandlers}
      >
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          progress={progress}
        />
        <div
          className="p-3 transition-transform w-full max-w-full"
          style={{ transform: `translateY(${pullDistance}px)` }}
        >
          {selectionMode === "tables" ? (
            // Tables grid
            tablesLoading ? (
              <div className="flex items-center justify-center h-40">
                <p className="text-muted-foreground">Carregando mesas...</p>
              </div>
            ) : activeTables.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <Users className="h-12 w-12 text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Nenhuma mesa cadastrada</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {activeTables.map((table) => (
                  <button
                    key={table.id}
                    onClick={() => handleSelectTable(table)}
                    className={cn(
                      "aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-1 min-w-0 transition-all active:scale-95",
                      table.status === "available"
                        ? "bg-success/10 border-success text-success hover:bg-success/20"
                        : table.status === "occupied"
                        ? "bg-warning/10 border-warning text-warning hover:bg-warning/20"
                        : "bg-muted border-muted-foreground/30"
                    )}
                  >
                    <span className="text-lg sm:text-xl font-bold">{table.number}</span>
                    {table.name ? (
                      <span className="text-[9px] sm:text-[10px] font-medium truncate max-w-full px-0.5">
                        {table.name}
                      </span>
                    ) : (
                      <span className="text-[9px] sm:text-[10px] uppercase tracking-wide opacity-80">
                        {table.status === "available"
                          ? "Livre"
                          : table.status === "occupied"
                          ? "Ocupada"
                          : "Reservada"}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )
          ) : (
            // Commands grid
            commandsLoading ? (
              <div className="flex items-center justify-center h-40">
                <p className="text-muted-foreground">Carregando comandas...</p>
              </div>
            ) : usableCommands.length === 0 && requireEntry ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <AlertTriangle className="h-8 w-8 text-muted-foreground/50 mb-2" />
                <p className="text-muted-foreground text-sm">Nenhuma comanda aberta.</p>
                <p className="text-muted-foreground text-xs">Vá em "Abertura de Comandas" para liberar.</p>
              </div>
            ) : activeCommands.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-40 text-center">
                <CreditCard className="h-12 w-12 text-muted-foreground/30 mb-2" />
                <p className="text-muted-foreground">Nenhuma comanda cadastrada</p>
              </div>
            ) : (
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
                {(requireEntry ? usableCommands : activeCommands).map((command) => {
                  const entry = getEntryForCommand(command.number);
                  const isUsable = !requireEntry || hasActiveEntry(command.number) || command.status === "in_use";
                  
                  return (
                    <button
                      key={command.id}
                      onClick={() => handleSelectCommand(command)}
                      disabled={!isUsable}
                      className={cn(
                        "aspect-square rounded-lg border-2 flex flex-col items-center justify-center p-1 min-w-0 transition-all active:scale-95",
                        !isUsable
                          ? "bg-muted border-muted-foreground/30 text-muted-foreground opacity-50 cursor-not-allowed"
                          : command.status === "available"
                            ? "bg-success/10 border-success text-success hover:bg-success/20"
                            : "bg-warning/10 border-warning text-warning hover:bg-warning/20"
                      )}
                    >
                      <span className="text-lg sm:text-xl font-bold">{command.number}</span>
                      {command.customer_name ? (
                        <span className="text-[9px] sm:text-[10px] font-medium truncate max-w-full px-0.5">
                          {command.customer_name}
                        </span>
                      ) : entry && command.status !== "in_use" ? (
                        <span className="text-[9px] sm:text-[10px] font-medium truncate max-w-full px-0.5 text-primary">
                          {entry.entry_type_name}
                        </span>
                      ) : (
                        <span className="text-[9px] sm:text-[10px] uppercase tracking-wide opacity-80">
                          {command.status === "available" ? "Livre" : "Em uso"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );

  // Render products view
  const renderProductsView = () => (
    <div className="flex flex-col h-full w-full min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="p-3 border-b bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-primary-foreground hover:bg-primary-foreground/20"
            onClick={handleBack}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold">{selectedItemLabel}</h1>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/20"
                onClick={() => setNicknameDialogOpen(true)}
              >
                <Edit2 className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="text-xs opacity-80 truncate">
              {selectedItemName || "Adicionar itens ao pedido"}
            </p>
          </div>
          
          {/* Action buttons for occupied table/command */}
          {isSelectedItemOccupied && sentItems.length > 0 && (
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={() => setShowConsumptionDialog(true)}
                title="Ver Consumo"
              >
                <Eye className="h-4 w-4" />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-primary-foreground hover:bg-primary-foreground/20"
                onClick={handlePrintConference}
                disabled={isPrintingConference}
                title="Imprimir Conferência"
              >
                <Printer className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {cartItemCount > 0 && (
            <Button
              size="sm"
              variant="secondary"
              className="gap-1"
              onClick={() => setViewMode("cart")}
            >
              <ShoppingBag className="h-4 w-4" />
              <span>{cartItemCount}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={productViewTab === "products" ? "Buscar produto..." : "Buscar combo..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
      </div>

      {/* Products/Combos Toggle */}
      <div className="border-b p-2 flex gap-1">
        <Button
          size="sm"
          variant={productViewTab === "products" ? "default" : "outline"}
          onClick={() => setProductViewTab("products")}
          className="flex-1 gap-1 h-8"
        >
          <ShoppingBag className="h-4 w-4" />
          Produtos
        </Button>
        <Button
          size="sm"
          variant={productViewTab === "combos" ? "default" : "outline"}
          onClick={() => setProductViewTab("combos")}
          className="flex-1 gap-1 h-8"
        >
          <Package className="h-4 w-4" />
          Combos
          {combos.length > 0 && (
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {combos.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Categories (only for products tab) */}
      {productViewTab === "products" && (
        <div className="border-b p-2">
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant={selectedCategory === "all" ? "default" : "outline"}
              onClick={() => setSelectedCategory("all")}
              className="text-xs h-7 px-2"
            >
              Todos
            </Button>
            {activeCategories.map((cat) => {
              const isSelected = selectedCategory === cat.id;
              const IconComponent = getIconComponent(cat.icon);
              const colorStyles = getColorStyles(cat.color, isSelected);
              
              return (
                <Button
                  key={cat.id}
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={cn(
                    "text-xs h-7 px-2 gap-1 border transition-colors",
                    colorStyles
                  )}
                >
                  <IconComponent className="h-3.5 w-3.5" />
                  {cat.name}
                </Button>
              );
            })}
          </div>
        </div>
      )}

      {/* Products or Combos Grid */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-2 w-full">
        {productViewTab === "products" ? (
          productsLoading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-muted-foreground">Carregando produtos...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-center">
              <ShoppingBag className="h-12 w-12 text-muted-foreground/30 mb-2" />
              <p className="text-muted-foreground">Nenhum produto encontrado</p>
            </div>
          ) : (
            <GarcomProductGrid
              products={filteredProducts}
              getCartQuantity={getCartQuantity}
              addToCart={addToCart}
              removeFromCart={removeFromCart}
              openNoteDialog={openNoteDialog}
              formatCurrency={formatCurrency}
            />
          )
        ) : (
          combosLoading ? (
            <div className="flex items-center justify-center h-40">
              <p className="text-muted-foreground">Carregando combos...</p>
            </div>
          ) : (
            <GarcomComboGrid
              combos={combos}
              getComboCartCount={getComboCartCount}
              onComboClick={handleComboClick}
              formatCurrency={formatCurrency}
            />
          )
        )}
      </div>

      {/* Bottom bar */}
      {cartItemCount > 0 && (
        <div className="p-3 border-t bg-card">
          <Button className="w-full h-12 text-base gap-2" onClick={() => setViewMode("cart")}>
            <ShoppingBag className="h-5 w-5" />
            Ver Pedido ({cartItemCount} itens) - {formatCurrency(cartTotal)}
          </Button>
        </div>
      )}
    </div>
  );

  // Render cart view
  const renderCartView = () => (
    <div className="flex flex-col h-full w-full min-w-0 overflow-x-hidden">
      {/* Header */}
      <div className="p-3 border-b bg-primary text-primary-foreground">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="text-primary-foreground hover:bg-primary-foreground/20"
            onClick={handleBack}
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-bold">Confirmar Pedido</h1>
            <p className="text-xs opacity-80">{selectedItemLabel}</p>
          </div>
        </div>
      </div>

      {/* Cart items */}
      <ScrollArea className="flex-1 p-3 w-full">
        <div className="space-y-2">
          {/* Regular product items */}
          {cart.map((item) => (
            <Card key={item.product.id} className="p-3">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <p className="font-medium text-sm">{item.product.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatCurrency(Number(item.product.price))} cada
                  </p>
                  {item.notes && (
                    <p className="text-xs text-warning mt-1 italic">
                      Obs: {item.notes}
                    </p>
                  )}
                </div>
                <p className="font-bold text-primary">
                  {formatCurrency(Number(item.product.price) * item.quantity)}
                </p>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={() => removeFromCart(item.product.id)}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <span className="w-8 text-center font-bold">{item.quantity}</span>
                  <Button
                    size="icon"
                    variant="outline"
                    className="h-8 w-8"
                    onClick={() => addToCart(item.product)}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => openNoteDialog(item.product)}
                >
                  {item.notes ? "Editar Obs" : "Add Obs"}
                </Button>
              </div>
            </Card>
          ))}

          {/* Combo items */}
          {comboCart.map((item, index) => {
            // Build selection summary
            let selectionSummary = "";
            if (item.selection.slotSelections) {
              selectionSummary = item.selection.slotSelections
                .map(s => s.selectedProducts.map(p => p.productName).join(", "))
                .join(" • ");
            } else if (item.selection.selectedFlavors) {
              selectionSummary = item.selection.selectedFlavors.map(f => f.productName).join(", ");
            } else if (item.selection.selectedChoices) {
              selectionSummary = item.selection.selectedChoices.map(c => c.productName).join(", ");
            }

            return (
              <Card key={`combo-${index}`} className="p-3 border-primary/30">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-0.5">
                      <Package className="h-3.5 w-3.5 text-primary" />
                      <Badge variant="secondary" className="text-[10px] h-4 px-1">Combo</Badge>
                    </div>
                    <p className="font-medium text-sm">{item.combo.name}</p>
                    {selectionSummary && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                        {selectionSummary}
                      </p>
                    )}
                    {item.notes && (
                      <p className="text-xs text-warning mt-1 italic">
                        Obs: {item.notes}
                      </p>
                    )}
                  </div>
                  <p className="font-bold text-primary">
                    {formatCurrency(item.calculatedPrice * item.quantity)}
                  </p>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => updateComboQuantity(index, item.quantity - 1)}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <span className="w-8 text-center font-bold">{item.quantity}</span>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => updateComboQuantity(index, item.quantity + 1)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-destructive hover:text-destructive"
                    onClick={() => removeComboFromCart(index)}
                  >
                    Remover
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      </ScrollArea>

      {/* Order notes and submit */}
      <div className="p-4 border-t bg-card space-y-3">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Observações gerais do pedido
          </label>
          <Textarea
            placeholder="Ex: cliente com pressa, aniversário, pedido urgente..."
            value={orderNotes}
            onChange={(e) => setOrderNotes(e.target.value)}
            rows={2}
            className="resize-none"
          />
        </div>
        <div className="flex justify-between text-lg font-bold">
          <span>Total</span>
          <span className="text-primary">{formatCurrency(cartTotal)}</span>
        </div>
        <Button
          className={cn(
            "w-full h-14 text-lg gap-2 transition-all",
            submitStatus === "success" && "bg-green-600 hover:bg-green-600",
            submitStatus === "error" && "bg-destructive hover:bg-destructive"
          )}
          onClick={handleSubmitOrder}
          disabled={isSubmitting}
        >
          {submitStatus === "sending" ? (
            <>
              <RefreshCw className="h-5 w-5 animate-spin" />
              Enviando pedido...
            </>
          ) : submitStatus === "success" ? (
            <>
              <Check className="h-5 w-5" />
              Enviado!
            </>
          ) : submitStatus === "error" ? (
            <>
              <AlertTriangle className="h-5 w-5" />
              Falha - Tente novamente
            </>
          ) : (
            <>
              <Check className="h-5 w-5" />
              Enviar Pedido
            </>
          )}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="h-[100dvh] md:h-screen flex flex-col bg-background overflow-x-hidden w-full min-w-0">
      {viewMode === "selection" && renderSelectionView()}
      {viewMode === "products" && renderProductsView()}
      {viewMode === "cart" && renderCartView()}

      {/* Note dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Observação do Item</DialogTitle>
          </DialogHeader>
          <Textarea
            placeholder="Ex: sem cebola, bem passado..."
            value={itemNote}
            onChange={(e) => setItemNote(e.target.value)}
            rows={3}
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={saveNote}>Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nickname dialog */}
      <TableNicknameDialog
        open={nicknameDialogOpen}
        onOpenChange={setNicknameDialogOpen}
        tableNumber={selectedTable?.number || selectedCommand?.number || ""}
        currentName={selectedItemName || null}
        onSave={async (name) => {
          if (selectedTable) {
            await updateTable(selectedTable.id, { name });
            setSelectedTable({ ...selectedTable, name });
            toast.success(name ? "Apelido salvo!" : "Apelido removido!");
          } else if (selectedCommand) {
            await updateCommand(selectedCommand.id, { customer_name: name || null });
            setSelectedCommand({ ...selectedCommand, customer_name: name || null });
            toast.success(name ? "Nome do cliente salvo!" : "Nome removido!");
          }
        }}
      />

      {/* Consumption dialog */}
      <Dialog open={showConsumptionDialog} onOpenChange={setShowConsumptionDialog}>
        <DialogContent className="max-w-sm max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              Consumo - {selectedItemLabel}
            </DialogTitle>
          </DialogHeader>
          
          <ScrollArea className="flex-1 max-h-[50vh]">
            {loadingSentItems ? (
              <p className="text-center text-muted-foreground py-4">Carregando...</p>
            ) : sentItems.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                Nenhum item lançado ainda
              </p>
            ) : (
              <div className="space-y-2">
                {sentItems.map((item) => (
                  <div key={item.id} className="flex justify-between text-sm p-2 border-b">
                    <div className="flex-1">
                      <span className="font-medium">{item.quantity}x</span>{" "}
                      <span>{item.product_name}</span>
                      {item.notes && (
                        <p className="text-xs text-muted-foreground italic">{item.notes}</p>
                      )}
                    </div>
                    <span className="font-bold">{formatCurrency(Number(item.total_price))}</span>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
          
          <div className="pt-3 border-t space-y-3">
            <div className="flex justify-between text-lg font-bold">
              <span>Total</span>
              <span className="text-primary">{formatCurrency(sentItemsTotal)}</span>
            </div>
            <Button
              className="w-full gap-2"
              onClick={handlePrintConference}
              disabled={isPrintingConference || sentItems.length === 0}
            >
              <Printer className="h-4 w-4" />
              {isPrintingConference ? "Enviando..." : "Imprimir Conferência"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* People Count Dialog */}
      <PeopleCountDialog
        open={showPeopleDialog}
        onOpenChange={(open) => {
          setShowPeopleDialog(open);
          if (!open) setPendingSelection(null);
        }}
        destinationType={pendingSelection?.type === "table" ? "table" : "tab"}
        destinationNumber={pendingSelection?.type === "table" 
          ? (pendingSelection.item as RestaurantTable).number 
          : pendingSelection?.type === "command" 
            ? (pendingSelection.item as Command).number 
            : ""}
        onConfirm={handlePeopleConfirm}
      />

      {/* Combo Detail Sheet */}
      <ComboDetailSheet
        key={selectedCombo?.id}
        combo={selectedCombo}
        open={comboSheetOpen}
        onOpenChange={setComboSheetOpen}
        onAddToCart={handleAddComboToCart}
      />

      {/* PWA Install Prompt */}
      <WidgetErrorBoundary>
        <InstallPrompt />
      </WidgetErrorBoundary>
    </div>
  );
}
